// H-14, H-19, H-20: the round's candidates.
//
// The list, the invite, the ranking and the compare. Two points worth keeping
// in mind when this screen changes:
//
//   RANKING IS MANUAL (H-20, L-3). There is no "sort by level" and there must
//   not be: a score that effectively orders candidates is an automated decision
//   under GDPR and the UK rules. Owners move people by hand, there is one
//   shared ranking per round, and a save carries the version it was read at so
//   two owners cannot silently overwrite each other.
//
//   A LEVEL IS NOT A SCORE. The row shows each capability's level, never a
//   total, an average or a percentage, because a single number is the thing
//   that turns evidence into a decision.

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { DragDropContext, Draggable, Droppable, type DraggableProvided, type DropResult } from '@hello-pangea/dnd';
import { LuGripVertical } from 'react-icons/lu';
import { Box, Typography } from '@mui/material';
import { Bone, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import PinnedScrollbar, { HIDDEN_X_SCROLLBAR } from '../../../../components/ui/PinnedScrollbar';
import {
  hiringApi,
  hiringMessage,
  type CopyBlock,
  type HiringCandidateView,
  type HiringRoundView
} from '../../../../api/hiring';
import { useHiringCandidates, useHiringOrg, useRefreshHiring } from '../../../../queries/useHiring';
import {
  ActionButton,
  BODY,
  BRANDY,
  DANGER,
  FONT,
  LABEL,
  Note,
  SURFACE,
  TEXT,
  TEXT_2,
  TEXT_3,
  TEXT_4
} from '../capability-setup/designSystem';
import { capabilityColor } from '../Program/components/learning-lifecycle/teamCapability';
import {
  ButtonSlot,
  Empty,
  GRID_LINE,
  HEAD_LINE,
  LevelText,
  PANEL_SX,
  ROUND_BUTTON_WIDTHS,
  RowButton,
  StatusText
} from './hiringParts';
import { exportRoundCsv } from './hiringExport';
import HireDialog from './HireDialog';
import InviteDialog, { type InviteRow } from './InviteDialog';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

// The table, in the shape Insights uses (LevelMatrix): set columns, then one per
// capability that shares what is left. Every row carries the same template rather
// than sitting in one shared grid, because a row being dragged leaves the table and
// has to keep its columns.
// The Actions column fits two of its buttons, which are all one size.
const ACTIONS_COLUMN = 170;
// Rank, Name, Status, Completion, Analysis, Actions.
const FIXED_COLUMNS = [68, 170, 120, 120, 120, ACTIONS_COLUMN];
// One width for every capability: "Handle Objections" fits it, and a longer title is cut.
const CAPABILITY_COLUMN = 156;
// A round always shows this many capability columns, empty ones included, so the table
// keeps its shape however few a role has.
const CAPABILITY_SLOTS = 8;
const FIXED_WIDTH = FIXED_COLUMNS.reduce((sum, width) => sum + width, 0);
// Everything up to the capabilities stays in view: only the capabilities move sideways,
// as on Insights. Each cell sticks at its column's edge.
const PINNED_LEFT = FIXED_COLUMNS.map((_, i) => FIXED_COLUMNS.slice(0, i).reduce((sum, width) => sum + width, 0));
const LAST_FIXED = FIXED_COLUMNS.length - 1;
// The brandy line Insights draws where the capabilities start.
const BRANDY_RULE = '1px solid rgba(247, 148, 32, 0.45)';
const FIRST_CAPABILITY = { '&&': { borderLeft: 'none' } };
// A row's fill, as a variable so its pinned cells follow it when it is selected.
const ROW_FILL = `var(--row-fill, ${SURFACE})`;
const SELECTED_FILL = '#33261A';
// Candidates who are out of the running: what they did stays readable, and reads as over.
const FAILED_STATUSES = ['unsuccessful', 'removed'];
const FAILED_CELL = { '& > *': { opacity: 0.45 } };
const UNRANKED_ORDER: Array<HiringCandidateView['status']> = ['expired', 'unsuccessful', 'removed'];

// Any width left over on a very wide screen is shared by the capabilities.
const gridFor = (columns: number) => ({
  template: [
    ...FIXED_COLUMNS.map((width) => `${width}px`),
    `repeat(${columns}, minmax(${CAPABILITY_COLUMN}px, 1fr))`
  ].join(' '),
  minWidth: FIXED_WIDTH + columns * CAPABILITY_COLUMN
});

const rowSx = (template: string) => ({
  display: 'grid',
  gridTemplateColumns: template,
  boxSizing: 'border-box' as const,
  minHeight: 50,
  bgcolor: ROW_FILL,
  '& > * + *': { borderLeft: GRID_LINE }
});
const cellSx = { display: 'flex', alignItems: 'center', px: '10px', minWidth: 0 };
const centredSx = { ...cellSx, justifyContent: 'center', textAlign: 'center' as const };
const pinnedSx = (column: number) => ({
  position: 'sticky' as const,
  left: PINNED_LEFT[column],
  zIndex: 1,
  bgcolor: ROW_FILL,
  ...(column === LAST_FIXED ? { borderRight: BRANDY_RULE } : null),
  [MOBILE_SX]: { position: 'static' as const }
});
const cutSx = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const };
const linkSx = {
  background: 'none',
  border: 0,
  p: 0,
  cursor: 'pointer',
  fontFamily: FONT,
  fontSize: 12.5,
  color: BRANDY,
  textDecoration: 'underline',
  textUnderlineOffset: '3px'
};
const QUIET = <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_4 }}>-</Typography>;

const CandidatesHeader: React.FC<{
  template: string;
  capabilities: Array<{ id: string; name: string }>;
  emptySlots: number;
}> = ({ template, capabilities, emptySlots }) => {
  const head = { ...LABEL, color: TEXT, whiteSpace: 'nowrap' as const };
  const titles = ['Status', 'Completion', 'Analysis', 'Actions'];
  return (
    <Box sx={{ ...rowSx(template), borderBottom: HEAD_LINE, position: 'sticky', top: 0, zIndex: 2 }}>
      <Box sx={{ ...head, ...centredSx, ...pinnedSx(0) }}>Rank</Box>
      <Box sx={{ ...head, ...cellSx, ...pinnedSx(1) }}>Name</Box>
      {titles.map((title, i) => (
        <Box key={title} sx={{ ...head, ...centredSx, ...pinnedSx(i + 2) }}>
          {title}
        </Box>
      ))}
      {capabilities.map((capability, i) => (
        <Box
          key={capability.id}
          title={capability.name}
          sx={{ ...head, ...centredSx, ...(i === 0 ? FIRST_CAPABILITY : null) }}
        >
          <Box component="span" sx={cutSx}>
            {capability.name}
          </Box>
        </Box>
      ))}
      {Array.from({ length: emptySlots }, (_, i) => (
        <Box key={i} sx={capabilities.length === 0 && i === 0 ? FIRST_CAPABILITY : undefined} />
      ))}
    </Box>
  );
};

/**
 * One candidate in the table. Its own component because the Draggable render
 * prop would otherwise put this three callbacks deep, and because the unranked
 * rows render it too.
 */
const CandidateRow: React.FC<{
  candidate: HiringCandidateView;
  /** Their place in the shared ranking, or -1 when they do not rank. */
  position: number;
  /** The number the row shows: its place in the table, ranked or not. */
  rowNumber: number;
  template: string;
  capabilities: Array<{ id: string; name: string }>;
  emptySlots: number;
  last: boolean;
  /** The owner chose Compare Candidates: a click on the row now selects it. */
  selecting: boolean;
  picked: boolean;
  dragging?: boolean;
  drag?: DraggableProvided;
  /** What can be done with this candidate from the table: the end of the row. */
  actions: React.ReactNode;
  onPick: (id: string) => void;
  onOpen: (id: string) => void;
}> = ({
  candidate,
  position,
  rowNumber,
  template,
  capabilities,
  emptySlots,
  last,
  selecting,
  picked,
  dragging,
  drag,
  actions,
  onPick,
  onOpen,
}) => {
  const levels = new Map(candidate.capabilities.map((c) => [c.capabilityId, c.level]));
  const failed = FAILED_STATUSES.includes(candidate.status) ? FAILED_CELL : null;
  const selectProps = selecting
    ? {
        role: 'checkbox',
        'aria-checked': picked,
        'aria-label': `Select ${candidate.name} to compare`,
        tabIndex: 0,
        onClick: () => onPick(candidate.id),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onPick(candidate.id);
          }
        }
      }
    : {};
  return (
    <Box
      ref={drag?.innerRef}
      {...(drag?.draggableProps || {})}
      {...selectProps}
      sx={{
        ...rowSx(template),
        ...(picked && selecting ? { '--row-fill': SELECTED_FILL } : null),
        borderBottom: last ? 'none' : GRID_LINE,
        outline: dragging ? `1px solid ${BRANDY}` : 'none',
        boxShadow: dragging ? '0 12px 28px rgba(0,0,0,0.5)' : 'none',
        // While selecting, the whole row is the target: the links inside it stand aside.
        ...(selecting ? { cursor: 'pointer', '& *': { pointerEvents: 'none' } } : null),
        ...(drag?.draggableProps?.style || {})
      }}
    >
      {/* Keyboard ranking comes with the handle: focus it, Space to lift, arrows to move,
          Space to drop. A hiring tool has to be operable without a mouse (L-5). */}
      <Box sx={{ ...centredSx, ...pinnedSx(0), ...failed, px: 0, gap: '2px' }}>
        {position >= 0 ? (
          <Box
            {...(drag?.dragHandleProps || {})}
            aria-label={`Drag ${candidate.name} to reorder`}
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 22,
              height: 28,
              borderRadius: '4px',
              color: TEXT_4,
              cursor: 'grab',
              '&:hover': { color: TEXT_2, bgcolor: 'rgba(255,255,255,0.04)' },
              '&:active': { cursor: 'grabbing' }
            }}
          >
            <LuGripVertical size={15} />
          </Box>
        ) : (
          // No handle, as they do not rank; the space keeps the numbers in one line.
          <Box sx={{ width: 22 }} />
        )}
        <Typography sx={{ ...LABEL, color: TEXT, fontSize: 12, minWidth: 16, textAlign: 'center' }}>
          {rowNumber}
        </Typography>
      </Box>
      <Box sx={{ ...cellSx, ...pinnedSx(1), ...failed }}>
        <Box
          component="button"
          type="button"
          title={candidate.name}
          onClick={() => onOpen(candidate.id)}
          sx={{
            ...cutSx,
            minWidth: 0,
            textAlign: 'left',
            background: 'none',
            border: 0,
            p: 0,
            cursor: 'pointer',
            fontFamily: FONT,
            fontSize: 13.5,
            fontWeight: 600,
            color: TEXT
          }}
        >
          {candidate.name}
        </Box>
      </Box>
      <Box sx={{ ...centredSx, ...pinnedSx(2), ...failed }}>
        <StatusText status={candidate.status} />
      </Box>
      <Box sx={{ ...centredSx, ...pinnedSx(3), ...failed }}>
        <Typography
          sx={{
            ...BODY,
            fontSize: 12.5,
            fontWeight: 600,
            color: capabilityColor(candidate.progress),
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {candidate.progress}%
        </Typography>
      </Box>
      <Box sx={{ ...centredSx, ...pinnedSx(4), ...failed }}>
        <Box
          component="button"
          type="button"
          aria-label={`Open the analysis of ${candidate.name}`}
          onClick={() => onOpen(candidate.id)}
          sx={linkSx}
        >
          View
        </Box>
      </Box>
      <Box sx={{ ...centredSx, ...pinnedSx(5), gap: '6px', px: '6px' }}>{actions || QUIET}</Box>
      {capabilities.map((capability, i) => (
        <Box key={capability.id} sx={{ ...centredSx, ...failed, ...(i === 0 ? FIRST_CAPABILITY : null) }}>
          <LevelText level={levels.get(capability.id) ?? null} />
        </Box>
      ))}
      {Array.from({ length: emptySlots }, (_, i) => (
        <Box
          key={i}
          sx={{ ...centredSx, ...failed, ...(capabilities.length === 0 && i === 0 ? FIRST_CAPABILITY : null) }}
        >
          {QUIET}
        </Box>
      ))}
    </Box>
  );
};

const RoundCandidates: React.FC<{
  round: HiringRoundView;
  onOpenCandidate: (candidateId: string) => void;
  onCompare: (ids: string[]) => void;
  teams: Array<{ id: string; name: string }>;
  roles: Array<{ id: string; name: string; teamId: string }>;
  canHireAsAdmin: boolean;
  rationaleTooltip: CopyBlock | null;
  /** Where this tab's buttons go: the round's tab bar. */
  toolbar: HTMLElement | null;
  /** Where its Export goes: the round's header, above Compare Candidates. */
  exportSlot: HTMLElement | null;
}> = ({ round, onOpenCandidate, onCompare, teams, roles, canHireAsAdmin, rationaleTooltip, toolbar, exportSlot }) => {
  const org = useHiringOrg();
  const refresh = useRefreshHiring();
  const query = useHiringCandidates(round.id);
  const candidates = useMemo(() => query.data || [], [query.data]);
  const [inviting, setInviting] = useState(false);
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const [hiringId, setHiringId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [order, setOrder] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<Array<{ email: string; why: string }>>([]);

  // Only candidates still in the round rank, in the server's order first.
  const rankable = useMemo(
    () => candidates.filter((c) => ['invited', 'started', 'completed', 'hired'].includes(c.status)),
    [candidates]
  );
  // The order as the page opens it: what the owner's own moves are measured against, so
  // Save Ranking only wakes up once they have changed something.
  const savedOrder = useMemo(() => {
    const ranked = round.ranking.filter((id) => rankable.some((c) => c.id === id));
    const rest = rankable.map((c) => c.id).filter((id) => !ranked.includes(id));
    return [...ranked, ...rest];
  }, [round.ranking, rankable]);
  useEffect(() => setOrder(savedOrder), [savedOrder]);

  const pick = (id: string) =>
    setPicked((current) => (current.includes(id) ? current.filter((one) => one !== id) : [...current, id].slice(-2)));
  const stopSelecting = () => {
    setSelecting(false);
    setPicked([]);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    setOrder((current) => {
      const copy = [...current];
      copy.splice(result.destination!.index, 0, copy.splice(result.source.index, 1)[0]);
      return copy;
    });
  };

  const run = async (label: string, work: () => Promise<unknown>, after?: () => void) => {
    setBusy(label);
    setError(null);
    try {
      await work();
      refresh({ roundId: round.id });
      after?.();
    } catch (err) {
      setError(hiringMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const invite = async (rows: InviteRow[]) => {
    setBusy('invite');
    setError(null);
    setRefused([]);
    try {
      const result = await hiringApi.invite(round.id, rows, org);
      refresh({ roundId: round.id });
      setRefused(result.refused);
      if (result.refused.length === 0) setInviting(false);
    } catch (err) {
      setError(hiringMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const orderChanged = order.join(',') !== savedOrder.join(',');
  const byId = new Map(candidates.map((c) => [c.id, c]));
  // Below the ranking: expired invites, then Unsuccessful, then Removed at the very bottom.
  const unranked = candidates
    .filter((candidate) => !order.includes(candidate.id))
    .sort((a, b) => UNRANKED_ORDER.indexOf(a.status) - UNRANKED_ORDER.indexOf(b.status));
  const rankedCount = order.filter((id) => byId.has(id)).length;
  const rowCount = rankedCount + unranked.length;
  // One column per capability the round assesses, whoever has been graded on it so far.
  const capabilities = useMemo(() => {
    const columns = new Map(round.items.map((item) => [item.capabilityId, item.capabilityName]));
    candidates.forEach((c) =>
      c.capabilities.forEach((capability) => {
        if (!columns.has(capability.capabilityId)) columns.set(capability.capabilityId, capability.capabilityName);
      })
    );
    return Array.from(columns, ([id, name]) => ({ id, name }));
  }, [round.items, candidates]);
  const emptySlots = Math.max(0, CAPABILITY_SLOTS - capabilities.length);
  const grid = gridFor(capabilities.length + emptySlots);
  const hiringCandidate = hiringId ? byId.get(hiringId) : undefined;

  // The everyday actions, on the row, all one size. A closed round is read-only except for
  // removing an expired invite, and Delete Entirely stays in the Analysis modal, behind its
  // typed confirmation (H-22).
  const rowActions = (candidate: HiringCandidateView): React.ReactNode => {
    if (round.status !== 'open' && candidate.status !== 'expired') return null;
    const act = (name: string, label: string, work: () => Promise<unknown>, brandy = false) => (
      <RowButton
        key={name}
        brandy={brandy}
        disabled={busy === `${name}:${candidate.id}`}
        onClick={() => run(`${name}:${candidate.id}`, work)}
      >
        {label}
      </RowButton>
    );
    // Removing takes them out of the round, so it asks first.
    if (removingId === candidate.id) {
      return (
        <>
          <RowButton
            brandy
            disabled={busy === `remove:${candidate.id}`}
            onClick={() =>
              run(
                `remove:${candidate.id}`,
                () => hiringApi.removeCandidate(candidate.id, org),
                () => setRemovingId(null)
              )
            }
          >
            Confirm
          </RowButton>
          <RowButton disabled={busy === `remove:${candidate.id}`} onClick={() => setRemovingId(null)}>
            Cancel
          </RowButton>
        </>
      );
    }
    const remove = (
      <RowButton key="remove" onClick={() => setRemovingId(candidate.id)}>
        Remove
      </RowButton>
    );
    // A new invitation to a closed round would lead nowhere, so only Remove is left.
    if (round.status !== 'open') return remove;
    switch (candidate.status) {
      // Finished or not: the owner can decide on what they have seen so far.
      case 'started':
      case 'completed':
        return (
          <>
            <RowButton brandy onClick={() => setHiringId(candidate.id)}>
              Hire
            </RowButton>
            {remove}
          </>
        );
      case 'invited':
        return (
          <>
            {act('resend', 'Resend', () => hiringApi.resendInvite(candidate.id, org), true)}
            {act('revoke', 'Revoke', () => hiringApi.revokeInvite(candidate.id, org))}
          </>
        );
      // Resend is always a new invitation, so an expired one can be sent again.
      case 'expired':
        return (
          <>
            {act('resend', 'Resend', () => hiringApi.resendInvite(candidate.id, org), true)}
            {remove}
          </>
        );
      case 'removed':
        return act('restore', 'Restore', () => hiringApi.restoreCandidate(candidate.id, org));
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        [MOBILE_SX]: { flex: '0 0 auto' }
      }}
    >
      {error && (
        <Typography sx={{ ...BODY, color: DANGER, flexShrink: 0 }} role="alert">
          {error}
        </Typography>
      )}

      {/* These belong to this tab but sit in the round's tab bar, so they go when it does. */}
      {exportSlot &&
        createPortal(
          <ActionButton
            disabled={candidates.length === 0}
            reason={candidates.length === 0 ? 'There are no candidates to export yet' : undefined}
            onClick={() =>
              exportRoundCsv(round, capabilities, [
                ...(order.map((id) => byId.get(id)).filter(Boolean) as HiringCandidateView[]),
                ...unranked
              ])
            }
          >
            Export
          </ActionButton>,
          exportSlot
        )}
      {toolbar &&
        createPortal(
          <>
            <ButtonSlot width={ROUND_BUTTON_WIDTHS.right}>
              <ActionButton
                primary
                disabled={!orderChanged || busy === 'rank'}
                reason={!orderChanged ? 'Drag a candidate to change the ranking' : undefined}
                onClick={() => run('rank', () => hiringApi.setRanking(round.id, order, round.rankingVersion, org))}
              >
                {busy === 'rank' ? 'Saving...' : 'Save Ranking'}
              </ActionButton>
            </ButtonSlot>
            {selecting ? (
              <>
                <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_3 }}>
                  Select two candidates ({picked.length} of 2)
                </Typography>
                <ActionButton onClick={stopSelecting}>Cancel</ActionButton>
                <ActionButton
                  primary
                  disabled={picked.length !== 2}
                  reason={picked.length !== 2 ? 'Select two candidates to compare them' : undefined}
                  onClick={() => {
                    onCompare(picked);
                    stopSelecting();
                  }}
                >
                  Compare
                </ActionButton>
              </>
            ) : (
              <ButtonSlot width={ROUND_BUTTON_WIDTHS.left}>
                <ActionButton onClick={() => setSelecting(true)}>Compare Candidates</ActionButton>
              </ButtonSlot>
            )}
            <ButtonSlot width={ROUND_BUTTON_WIDTHS.right}>
              <ActionButton
                disabled={round.status !== 'open'}
                reason={round.status !== 'open' ? 'Candidates can only be invited to an open round' : undefined}
                onClick={() => setInviting(true)}
              >
                Invite Candidates
              </ActionButton>
            </ButtonSlot>
          </>,
          toolbar
        )}

      <Box role="tabpanel" sx={PANEL_SX}>
        {query.isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', ...fadeInSx }}>
            <Bone height={56} />
            <Bone height={56} />
          </Box>
        ) : candidates.length === 0 ? (
          <Empty>
            {round.status === 'open'
              ? 'No candidates yet. Invite the people you want to assess.'
              : 'This round was never opened, so nobody was invited.'}
          </Empty>
        ) : (
          <Box
            sx={{
              flex: '0 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              border: GRID_LINE,
              borderRadius: '4px',
              overflow: 'hidden'
            }}
          >
            <Box ref={setScroller} sx={{ flex: '0 1 auto', minHeight: 0, overflow: 'auto', ...HIDDEN_X_SCROLLBAR }}>
              <Box sx={{ minWidth: grid.minWidth }}>
                <CandidatesHeader template={grid.template} capabilities={capabilities} emptySlots={emptySlots} />
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="hiring-ranking">
                    {(dropArea) => (
                      <Box ref={dropArea.innerRef} {...dropArea.droppableProps}>
                        {(order.map((id) => byId.get(id)).filter(Boolean) as HiringCandidateView[]).map(
                          (candidate, index) => (
                            <Draggable
                              key={candidate.id}
                              draggableId={candidate.id}
                              index={index}
                              isDragDisabled={selecting}
                            >
                              {(drag, state) => (
                                <CandidateRow
                                  candidate={candidate}
                                  position={index}
                                  rowNumber={index + 1}
                                  template={grid.template}
                                  capabilities={capabilities}
                                  emptySlots={emptySlots}
                                  last={index === rowCount - 1}
                                  selecting={selecting}
                                  picked={picked.includes(candidate.id)}
                                  dragging={state.isDragging}
                                  drag={drag}
                                  actions={rowActions(candidate)}
                                  onPick={pick}
                                  onOpen={onOpenCandidate}
                                />
                              )}
                            </Draggable>
                          )
                        )}
                        {dropArea.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>

                {/* Removed and expired candidates are listed, and never ranked. */}
                {unranked.map((candidate, index) => (
                  <CandidateRow
                    key={candidate.id}
                    candidate={candidate}
                    position={-1}
                    rowNumber={rankedCount + index + 1}
                    template={grid.template}
                    capabilities={capabilities}
                    emptySlots={emptySlots}
                    last={index === unranked.length - 1}
                    actions={rowActions(candidate)}
                    selecting={selecting}
                    picked={picked.includes(candidate.id)}
                    onPick={pick}
                    onOpen={onOpenCandidate}
                  />
                ))}
              </Box>
            </Box>
            <PinnedScrollbar scroller={scroller} left={FIXED_WIDTH} />
          </Box>
        )}
      </Box>

      <Note>
        Elliott does not rank candidates. Elliott only provides contextual assessments of candidates&apos; performance in
        capability roleplays. Ranking is yours to do by hand based on your own conclusions.
      </Note>

      {hiringCandidate && (
        <HireDialog
          open
          candidate={hiringCandidate}
          round={round}
          teams={teams}
          roles={roles}
          canHireAsAdmin={canHireAsAdmin}
          tooltip={rationaleTooltip}
          busy={busy === 'hire'}
          error={error}
          onHire={(input) =>
            run(
              'hire',
              () => hiringApi.hire(hiringCandidate.id, input, org),
              () => setHiringId(null)
            )
          }
          onClose={() => setHiringId(null)}
        />
      )}
      <InviteDialog
        open={inviting}
        busy={busy === 'invite'}
        error={error}
        refused={refused}
        onInvite={invite}
        onClose={() => {
          setInviting(false);
          setRefused([]);
          setError(null);
        }}
      />
    </Box>
  );
};

export default RoundCandidates;
